/**
 * A booking's page's standard look (signed-in-web.html, the prototype's booking
 * page): the breadcrumb and heading, then the ticket and its sections on the
 * left and the photo with the page's one action on the right.
 */
import { useId } from 'react';
import { SafeRichText } from '@/components/booking/SafeRichText';

import { partsOf, pieceRoot } from '../../contract/runtime';
import { Ticket } from '../booked/BookedView';
import { MessageStudio } from '../message-studio';
import account from '../../styles/account.module.css';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { onLink } from '../../ui/onLink';
import { Photo } from '../../ui/Photo';
import css from './BookingPage.module.css';
import type { BookingPageRuntime } from './useBookingPage';
import { contract } from './contract';

const part = partsOf(contract);

// A cancelled booking's status reads at full strength, a hollow dot, not dimmed (card 4 walk, Gabriel 2026-10-31).
const TONE = { live: '', done: account.statusDone, off: css.stOff } as const;

function CancelWindow({ state, actions }: BookingPageRuntime) {
  const titleId = useId();
  const w = state.cancelWindow;
  if (!w) return null;
  return (
    <div className={account.scrim} onClick={(e) => e.target === e.currentTarget && !w.busy && actions.closeCancel()}>
      <div className={account.dlg} role="dialog" aria-modal="true" aria-labelledby={titleId} {...part('cancelWindow')}>
        <div className={account.eyebrow}>{w.kicker}</div>
        <h2 className={account.dlgTitle} id={titleId}>
          {w.title}
        </h2>
        <p>
          Your <b>{w.date}</b>
          {w.rest}
        </p>
        {w.nudge && <p className={account.nudge}>{w.nudge}</p>}
        {w.policy && (
          <p className={account.policy} role={w.policy.state === 'checking' ? 'status' : 'note'}>
            {w.policy.state === 'ready' && <Icon name="undo" small />}
            <span>{w.policy.text}</span>
          </p>
        )}
        <div className={account.dlgBtns}>
          <button type="button" className={`${shared.btn} ${shared.btnOutline} ${account.btnSm}`} onClick={actions.closeCancel} disabled={w.busy}>
            {w.keepLabel}
          </button>
          <button type="button" className={`${shared.btn} ${account.btnDanger} ${account.btnSm}`} onClick={actions.confirmCancel} disabled={w.busy}>
            {w.yesLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BookingPageView(runtime: BookingPageRuntime) {
  const { state, actions } = runtime;
  if (state.status === 'error' && state.readError) {
    return (
      <div className={`${shared.wrap} ${css.page}`} {...pieceRoot(contract)} {...part('page')}>
        <div className={css.readError} role="status" {...part('readError')}>
          <h1 className={css.h1}>{state.readError.title}</h1>
          <p>{state.readError.text}</p>
          <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={actions.retry}>
            {state.readError.retryLabel}
          </button>
        </div>
      </div>
    );
  }
  if (state.status !== 'ready') return null;
  const go = (href: string) => onLink(() => actions.open(href));
  return (
    <div className={`${shared.wrap} ${css.page}`} {...pieceRoot(contract)} {...part('page')}>
      <nav className={css.crumbs} aria-label="Breadcrumb" {...part('crumbs')}>
        <a href={state.bookingsHref} onClick={go(state.bookingsHref)}>
          Bookings
        </a>
        <span className={css.sep}>/</span>
        <span className={css.here}>{state.name}</span>
      </nav>

      <div className={css.head} {...part('heading')}>
        <div>
          <div className={`${account.eyebrow} ${css.kicker}`}>{state.eyebrow}</div>
          <h1 className={css.h1}>{state.name}</h1>
          {state.withName && (
            <div className={css.with}>
              {state.withPhotoUrl && <img src={state.withPhotoUrl} alt="" />}
              <span>
                with <b>{state.withName}</b>
              </span>
            </div>
          )}
        </div>
        {/* A cancelled booking is said by its status and its Cancelled line, not by fading the page (card 4 walk, Gabriel 2026-10-31). */}
        <div className={`${css.when} ${state.tone === 'done' ? css.whenDim : ''}`}>
          <b>{state.when}</b>
          <span className={`${account.status} ${css.st} ${TONE[state.tone]}`}>{state.statusLabel}</span>
        </div>
      </div>

      <div className={css.grid}>
        <div>
          {state.notice && (
            <p className={css.notice} role="status" {...part('notice')}>
              <Icon name={state.notice.icon === 'clock' ? 'clock' : 'info'} small />
              <span>
                <b>{state.notice.title}</b> {state.notice.text}
              </span>
            </p>
          )}

          <div className={css.ticket}>
            <Ticket
              name={state.name}
              photo={null}
              withName={null}
              head={false}
              rows={state.rows}
              calendar={state.calendar && { ...state.calendar, download: actions.addToCalendar }}
              rowAction={(row) =>
                row.label === 'Location' && state.join ? (
                  <span className={css.join}>
                    {state.join.href && (
                      <a className={css.joinLink} href={state.join.href} target="_blank" rel="noopener noreferrer" {...part('join')}>
                        <Icon name="video" small />
                        {state.join.platform ? `Join on ${state.join.platform}` : 'Join'}
                      </a>
                    )}
                    {state.join.passcode && (
                      <button type="button" className={css.pass} onClick={actions.copyPasscode} aria-label="Copy passcode" {...part('passcode')}>
                        Passcode {state.join.passcode}
                        <Icon name="copy" small />
                      </button>
                    )}
                  </span>
                ) : null
              }
            />
            {state.refunds && (
              <div className={css.ledger} {...part('refund')}>
                {state.refunds.lines.map((l, i) => (
                  <div key={i} className={css.lline}>
                    <div>
                      <span>Refund</span>
                      <span className={css.chip}>{l.state}</span>
                      <div className={css.lwhen}>{l.when}</div>
                    </div>
                    <div className={css.money}>{l.amount}</div>
                  </div>
                ))}
                {state.refunds.net && (
                  <div className={`${css.lline} ${css.net}`}>
                    <div>Net paid</div>
                    <div className={css.money}>{state.refunds.net}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {state.preparation && (
            <div className={css.prose} {...part('preparation')}>
              <span className={`${account.eyebrow} ${css.proseHead}`}>{state.preparation.heading}</span>
              <SafeRichText html={state.preparation.html} className={css.body} />
            </div>
          )}
          {state.about && (
            <div className={css.prose} {...part('about')}>
              <span className={`${account.eyebrow} ${css.proseHead}`}>{state.about.heading}</span>
              <SafeRichText html={state.about.html} className={css.body} />
            </div>
          )}

          {/* The Questions block is the message-studio piece (web-studio-sites D15), so one rule of a site's styles it everywhere. */}
          <div className={css.chat}>
            <MessageStudio options={{ heading: state.chat.heading }} />
          </div>
        </div>

        <aside className={css.side}>
          <div className={css.photo}>{state.photo && <Photo photo={state.photo} />}</div>
          {state.errand && (
            <a className={`${shared.btn} ${shared.btnPrimary} ${account.btnBlock} ${css.errand}`} href={state.errand.href} onClick={go(state.errand.href)} {...part(state.errand.kind)}>
              {state.errand.label}
              <Icon name="arrow" />
            </a>
          )}
          {state.telegramNote && (
            <p className={css.sideNote} {...part('telegramNote')}>
              <Icon name="tg" small />
              <span>{state.telegramNote}</span>
            </p>
          )}
          {/* Cancel sits with the booking's other actions (Gabriel 2026-10-23). */}
          {state.cancelLabel && (
            <div className={css.cancel}>
              <button type="button" className={account.dangerLink} onClick={actions.openCancel} {...part('cancel')}>
                {state.cancelLabel}
              </button>
            </div>
          )}
          {state.cancelClosed && (
            <p className={css.cancelClosed} {...part('cancelClosed')}>
              {state.cancelClosed}
            </p>
          )}
          {state.problem && (
            <p className={css.problem} role="alert">
              {state.problem}
            </p>
          )}
        </aside>
      </div>

      <CancelWindow {...runtime} />
      {state.done && (
        <div className={account.toast} role="status" {...part('done')}>
          {state.done}
        </div>
      )}
    </div>
  );
}
